// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Link,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Flag as FlagIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  HelpOutline as HelpIcon,
  ContentCopy as ContentCopyIcon,
  School as SchoolIcon,
  MoneyOff as MoneyOffIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { reportingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

// Contact info constants
const PHONE_NUMBER = '1-855-730-1800';
const PHONE_NUMBER_RAW = '18557301800'; // For tel: and sms: links
const EMAIL_ADDRESS = 'check@secondclear.com';

// Quick Start Modal Content
const QuickStartModal = ({ open, onClose, onDontShowAgain }) => {
  const steps = [
    {
      label: 'Forward suspicious calls, texts, or emails',
      description: 'When you receive something questionable, forward it to us for review.',
    },
    {
      label: 'We analyze and highlight patterns',
      description: 'Our system identifies common fraud patterns and provides an objective analysis — informational only, no judgment.',
    },
    {
      label: 'Review your calm summary',
      description: 'You\'ll receive a clear, neutral summary of what we found. No alarm bells, no scare tactics.',
    },
    {
      label: 'You decide what to do next',
      description: 'We provide information. You maintain full control over any decisions or actions.',
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflowX: 'hidden', maxWidth: { xs: 'calc(100vw - 32px)', sm: '600px' }, m: { xs: 2, sm: 'auto' } }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Welcome to Second Clear</Typography>
          <Typography variant="body2" color="text.secondary">Getting started is easy</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Stepper orientation="vertical" sx={{ mt: 1 }}>
          {steps.map((step, index) => (
            <Step key={step.label} active={true}>
              <StepLabel>
                <Typography fontWeight={500}>{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ my: 3 }} />

        {/* Contact Methods - Prominent Display */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          How to submit for review:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              bgcolor: 'primary.50',
              borderColor: 'primary.main'
            }}
          >
            <EmailIcon color="primary" sx={{ fontSize: 32, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">Forward emails to:</Typography>
              <Link href={`mailto:${EMAIL_ADDRESS}`} underline="hover">
                <Typography variant="h6" color="primary.main" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, overflowWrap: 'break-word', wordBreak: 'break-all' }}>
                  {EMAIL_ADDRESS}
                </Typography>
              </Link>
            </Box>
          </Paper>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              bgcolor: 'primary.50',
              borderColor: 'primary.main'
            }}
          >
            <PhoneIcon color="primary" sx={{ fontSize: 32, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">Call or text:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Link href={`tel:+${PHONE_NUMBER_RAW}`} underline="hover">
                  <Typography variant="h6" color="primary.main" fontWeight={600}>
                    {PHONE_NUMBER}
                  </Typography>
                </Link>
                <Link href={`sms:+${PHONE_NUMBER_RAW}`} underline="hover" sx={{ ml: 1 }}>
                  <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
                    (tap to text)
                  </Typography>
                </Link>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Dial all 11 digits including the 1 at the beginning
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Response Times */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
          How fast is it?
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Typography variant="body2">📞 <strong>Calls:</strong> Real-time — we walk through it with you as you talk</Typography>
          <Typography variant="body2">💬 <strong>Texts:</strong> Usually under 15 seconds</Typography>
          <Typography variant="body2">📧 <strong>Emails:</strong> Usually under a minute</Typography>
        </Box>

        {/* What this is / isn't */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                What this is
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Objective pattern analysis<br/>
                • Calm, informational summaries<br/>
                • A second opinion you control
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                What this isn't
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Not a blocking service<br/>
                • Not making decisions for you<br/>
                • Not creating alarm or urgency
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button 
          size="small" 
          color="inherit"
          onClick={onDontShowAgain}
        >
          Don't show again
        </Button>
        <Button 
          variant="contained" 
          onClick={onClose}
          size="large"
        >
          Get Started
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Clickable Stat Card Component
const StatCard = ({ title, value, icon, color = 'primary', onClick, highlight = false }) => (
  <Card sx={{ 
    bgcolor: highlight ? 'action.hover' : 'inherit',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 3,
    }
  }}>
    <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" color={color === 'primary' ? 'inherit' : `${color}.main`}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {React.cloneElement(icon, { sx: { fontSize: 40 } })}
          </Box>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [testPromptDismissed, setTestPromptDismissed] = useState(
    () => localStorage.getItem('testPromptDismissed') === 'true'
  );
  const [copied, setCopied] = useState(false);
  const [moneySentDismissed, setMoneySentDismissed] = useState(false);
  const [piiDisclosedDismissed, setPiiDisclosedDismissed] = useState(false);

  useEffect(() => {
    loadData();
    checkFirstVisit();
  }, []);

  const checkFirstVisit = () => {
    const hasSeenQuickStart = localStorage.getItem('hasSeenQuickStart');
    if (!hasSeenQuickStart) {
      setShowQuickStart(true);
    }
  };

  const handleCloseQuickStart = () => {
    setShowQuickStart(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('hasSeenQuickStart', 'true');
    setShowQuickStart(false);
  };

  const handleOpenQuickStart = () => {
    setShowQuickStart(true);
  };

  const handleCopyNumber = async () => {
    try {
      await navigator.clipboard.writeText('+18557301800');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = '+18557301800';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDismissTestPrompt = () => {
    localStorage.setItem('testPromptDismissed', 'true');
    setTestPromptDismissed(true);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsRes, historyRes] = await Promise.all([
        reportingAPI.getStats(),
        reportingAPI.getHistory({ limit: 10, exclude_training: false }),
      ]);

      setStats(statsRes.data);
      setRecentMessages(historyRes.data.events || []);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (highRisk) => {
    return highRisk ? 'error' : 'success';
  };

  const getRiskLabel = (highRisk) => {
    return highRisk ? 'High Risk' : 'Low Risk';
  };

  // Navigation handlers for stat cards
  const handleTotalMessagesClick = () => {
    navigate('/messages');
  };

  const handleUnreadClick = () => {
    navigate('/messages?filter=unread');
  };

  const handleHighRiskClick = () => {
    navigate('/messages?filter=high-risk');
  };

  const handleFlaggedClick = () => {
    navigate('/messages?filter=flagged');
  };

  const handlePeopleClick = () => {
    navigate('/people');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const highRiskCount = stats?.signal_breakdown?.high_risk_count || 0;
  const totalMessages = stats?.total_events || 0;
  const clientCount = stats?.linked_clients_count || 0;
  const unreadCount = stats?.unread_count || 0;
  const flaggedCount = stats?.flagged_count || 0;
  const moneySentCount = stats?.unacknowledged_money_sent_count || 0;
  const piiDisclosedCount = stats?.unacknowledged_pii_disclosed_count || 0;

  // Check if user is a caregiver (not self-signup)
  const isCaregiver = ['owner', 'caregiver', 'self'].includes(user?.accountType);

  // Prepare chart data
  const activityData = (stats?.recent_activity || []).reverse().map(item => ({
    date: format(new Date(item.date), 'MMM d'),
    total: parseInt(item.message_count),
    highRisk: parseInt(item.high_risk_count || 0),
  }));

  const riskDistributionData = [
    { name: 'High Risk', value: highRiskCount, color: '#f44336' },
    { name: 'Low Risk', value: totalMessages - highRiskCount, color: '#4caf50' },
  ];

  const channelData = Object.entries(stats?.stats?.by_channel || {}).map(([channel, count]) => ({
    name: channel.toUpperCase(),
    messages: count,
  }));

  return (
    <Box>
      {/* Header with Help Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Dashboard</Typography>
        <Button 
          startIcon={<HelpIcon />} 
          onClick={handleOpenQuickStart}
          size="small"
        >
          How It Works
        </Button>
      </Box>

      {/* Quick Start Modal */}
      <QuickStartModal 
        open={showQuickStart} 
        onClose={handleCloseQuickStart}
        onDontShowAgain={handleDontShowAgain}
      />

      {/* Demo prompt — shows until dismissed */}
      {!testPromptDismissed && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            bgcolor: 'primary.50',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" fontWeight={700} gutterBottom>
              See How It Works
            </Typography>
            <IconButton size="small" onClick={handleDismissTestPrompt}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Text any message to{' '}
            <Link href={`sms:+${PHONE_NUMBER_RAW}`} underline="hover" fontWeight={600}>
              {PHONE_NUMBER}
            </Link>
            {' '}from your phone to see an example of how we analyze suspicious messages.
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyNumber}
              size="small"
            >
              {copied ? 'Copied!' : 'Copy Number'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleDismissTestPrompt}
              size="small"
            >
              Dismiss
            </Button>
          </Box>
        </Paper>
      )}

      {/* Money Sent Alert — highest priority */}
      {moneySentCount > 0 && !moneySentDismissed && (
        <Alert
          severity="error"
          icon={<MoneyOffIcon />}
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button color="inherit" size="small" onClick={() => navigate('/messages?filter=money_sent')}>
                Review Now
              </Button>
              <IconButton size="small" color="inherit" onClick={() => setMoneySentDismissed(true)} aria-label="dismiss">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <AlertTitle>Money Sent Alert</AlertTitle>
          {moneySentCount} message{moneySentCount !== 1 ? 's' : ''} where your loved one reported sending money.
        </Alert>
      )}

      {/* PII Disclosed Alert */}
      {piiDisclosedCount > 0 && !piiDisclosedDismissed && (
        <Alert
          severity="warning"
          icon={<SecurityIcon sx={{ color: '#ea580c' }} />}
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button color="inherit" size="small" onClick={() => navigate('/messages?filter=pii_disclosed')}>
                Review Now
              </Button>
              <IconButton size="small" color="inherit" onClick={() => setPiiDisclosedDismissed(true)} aria-label="dismiss">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <AlertTitle>Personal Information Shared</AlertTitle>
          {piiDisclosedCount} message{piiDisclosedCount !== 1 ? 's' : ''} where your loved one reported sharing personal information.
        </Alert>
      )}

      {/* High risk alert */}
      {highRiskCount > 0 && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />} 
          sx={{ mb: 3, cursor: 'pointer' }}
          onClick={handleHighRiskClick}
        >
          <strong>{highRiskCount} high-risk messages</strong> detected. Click to review.
        </Alert>
      )}

      {/* Stats cards - all clickable */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: isCaregiver ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 3 }, mb: 4 }}>
        <StatCard
          title="Total Messages"
          value={totalMessages}
          icon={<MessageIcon />}
          color="primary"
          onClick={handleTotalMessagesClick}
        />
        <StatCard
          title="Unread"
          value={unreadCount}
          icon={<MarkEmailUnreadIcon />}
          color="primary"
          onClick={handleUnreadClick}
          highlight={unreadCount > 0}
        />
        <StatCard
          title="High Risk"
          value={highRiskCount}
          icon={<WarningIcon />}
          color="error"
          onClick={handleHighRiskClick}
        />
        <StatCard
          title="Flagged"
          value={flaggedCount}
          icon={<FlagIcon />}
          color="warning"
          onClick={handleFlaggedClick}
        />
        {/* Only show People for caregiver accounts */}
        {isCaregiver && (
          <StatCard
            title="People"
            value={clientCount}
            icon={<PeopleIcon />}
            color="primary"
            onClick={handlePeopleClick}
          />
        )}
      </Box>

      {/* Charts */}
      {totalMessages > 0 && (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }} justifyContent="center">
          {/* Activity Over Time */}
          {activityData.length > 0 && (
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <Typography variant="h6" gutterBottom>
                  Message Activity (Last 30 Days)
                </Typography>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={activityData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#1976d2"
                        name="Total Messages"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="highRisk"
                        stroke="#f44336"
                        name="High Risk"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Risk Distribution */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ alignSelf: 'flex-start' }}>
                Risk Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={riskDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {riskDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Messages by Channel */}
          {channelData.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <Typography variant="h6" gutterBottom>
                  Messages by Channel
                </Typography>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={channelData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="messages" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Recent activity */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Recent Activity</Typography>
          <Button size="small" onClick={() => navigate('/messages')}>
            View All
          </Button>
        </Box>

        {recentMessages.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary" gutterBottom>
              No messages yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Forward a suspicious call, text, or email to{' '}
              <Link href={`mailto:${EMAIL_ADDRESS}`} underline="hover" fontWeight={600}>
                {EMAIL_ADDRESS}
              </Link>{' '}
              or{' '}
              <Link href={`tel:+${PHONE_NUMBER_RAW}`} underline="hover" fontWeight={600}>
                call
              </Link>
              /
              <Link href={`sms:+${PHONE_NUMBER_RAW}`} underline="hover" fontWeight={600}>
                text {PHONE_NUMBER}
              </Link>{' '}
              to get started.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Most reviews come back in under a minute. Calls are evaluated in real-time.
            </Typography>
          </Box>
        ) : (
          <List>
            {recentMessages.map((message, index) => (
              <React.Fragment key={message.id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  onClick={() => navigate('/messages')}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}
                >
                  <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2">
                        {message.user_name || 'Unknown User'} • {message.channel.toUpperCase()}
                      </Typography>
                      {message.is_training && (
                        <Chip
                          icon={<SchoolIcon />}
                          label="Training"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Chip
                      label={getRiskLabel(message.high_risk)}
                      color={getRiskColor(message.high_risk)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ width: '100%' }}>
                    {message.message_text?.substring(0, 100)}...
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {format(new Date(message.received_at), 'PPp')} • {message.signal_count} signals
                  </Typography>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;
